import type { AppDeclaree } from './app-declaree.js';
import { verifierInstance } from './validation.js';
import type { DeclarationInstance } from './resolution.js';

/**
 * LA PARTIE PURE DE L'ENTREE DE REGISTRE : ce qui ne depend d'aucun composant.
 *
 * POURQUOI ELLE EST SEPAREE DE `registre.tsx`. Le registre fabrique les deux
 * composants de l'app (l'ecran, les reglages), donc il importe tout le design
 * system ; les tests unitaires de ce package tournent sous Node sur le `dist/`,
 * sans navigateur ni `@taki/ui` complet. Ce qui se DECIDE (quelles actions de
 * dock, quelle declaration exposer, et surtout AUCUNE pour une App Canvas) vit
 * donc ici, prouvable seul ; `registre.tsx` ne fait qu'assembler.
 */

/** Une action de creation, dans le vocabulaire du shell (`AppActionDecl`). */
export interface ActionDuDock {
  id: string;
  label: string;
  /** Nom d'une icone `@taki/ui`, resolue par la map curatee du shell. */
  iconName: string;
}

/** Ce que l'entree expose au Studio client : la declaration brute du modele. */
export interface DeclarationDEntree {
  champs: unknown;
  ecrans: unknown;
  fiche: unknown;
  verifier: (declaration: DeclarationInstance) => void;
}

/**
 * LES ACTIONS ECRITES DANS LA FICHE, s'il y en a. Ce ne sont PAS les
 * raccourcis des portes : le shell les derive seul de la declaration
 * (`raccourcis.ts` de `@taki/shell`), celle de l'org pour une app lite, et
 * une app declaree n'a rien a ecrire pour que chaque creation de porte soit
 * proposee au dock. La fiche ne sert plus qu'a un geste qui n'est la creation
 * d'aucune porte. Seul le NOM de la clef change (`icon` dans la fiche,
 * `iconName` dans le contrat du shell).
 */
export function actionsDuDock(app: AppDeclaree): ActionDuDock[] {
  return (app.fiche.actions ?? []).map((action) => ({
    id: action.id,
    label: action.label,
    iconName: action.icon,
  }));
}

/**
 * LA DECLARATION QUE L'ENTREE PORTE, ou `undefined` pour une App Canvas.
 *
 * UNE APP CANVAS N'EN EXPOSE AUCUNE, et c'est ce qui la tient hors du Studio
 * client : il n'y a ni champ, ni porte, ni vue a personnaliser pour une org,
 * et lui montrer les constantes vides d'une App Canvas lui ferait proposer un
 * editeur de portes sur une app qui n'en a pas. Le shell tient deja une entree
 * sans declaration pour ce qu'elle est (les apps ecrites a la main).
 */
export function declarationDEntree(app: AppDeclaree): DeclarationDEntree | undefined {
  if (app.appCanvas !== null) return undefined;
  return {
    champs: app.champs,
    ecrans: app.ecrans,
    fiche: app.fiche,
    verifier: (declaration) => verifierInstance(app, declaration),
  };
}
